import PropTypes from "prop-types";

/**
 * RealisticMotor – High-fidelity 3D industrial motor component.
 * Features:
 * - Output drive shaft & flywheel rotation lines with speed scaling (speedScale / live value)
 * - Tightly cropped 318x220 viewBox without wasted margin
 * - Natural motor stator operating vibration / shaking hum
 * - Top terminal junction box pilot LED status indicator
 * - 100% photo-realistic presentation in dark and light modes
 */
const RealisticMotor = ({
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
  const spinDuration = isRunning ? `${Math.max(0.18, 0.85 / (speedScale || 1))}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
        animation: isRunning ? "scada-vibration-hum 0.15s infinite" : "none",
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/Motor.png"
        alt="SCADA Realistic Motor"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 318x220 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 318 220"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="motorLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          {/* Clip path for the cylindrical drive shaft (X: 193..225, Y: 76..124) */}
          <clipPath id="motorShaftClip">
            <rect x="193" y="76" width="32" height="48" rx="2" />
          </clipPath>
        </defs>

        {/* ── Rotating Drive Shaft Surface Motion (Speed-scaled) ── */}
        {isRunning && (
          <g clipPath="url(#motorShaftClip)">
            {/* Spinning horizontal shaft lines – vivid electric blue for visibility */}
            <g
              style={{
                animation: `scada-motor-shaft-spin ${spinDuration} linear infinite`,
              }}
            >
              <line
                x1="193"
                y1="82"
                x2="225"
                y2="82"
                stroke="#38bdf8"
                strokeWidth="2.5"
                strokeDasharray="8 5"
                opacity="1"
              />
              <line
                x1="193"
                y1="94"
                x2="225"
                y2="94"
                stroke="#7dd3fc"
                strokeWidth="2.8"
                strokeDasharray="10 6"
                opacity="0.9"
              />
              <line
                x1="193"
                y1="106"
                x2="225"
                y2="106"
                stroke="#38bdf8"
                strokeWidth="2.5"
                strokeDasharray="8 5"
                opacity="1"
              />
              <line
                x1="193"
                y1="118"
                x2="225"
                y2="118"
                stroke="#7dd3fc"
                strokeWidth="2.8"
                strokeDasharray="10 6"
                opacity="0.9"
              />
            </g>
          </g>
        )}

        {/* ── Rotating Flywheel / Coupling Bolt Orbit (Center: 233, 101) ── */}
        {isRunning && (
          <g
            style={{
              transformOrigin: "233px 101px",
              animation: `scada-spin ${spinDuration} linear infinite`,
            }}
          >
            <circle cx="233" cy="78" r="1.6" fill="#cbd5e1" opacity="0.8" />
            <circle cx="240" cy="88" r="1.6" fill="#cbd5e1" opacity="0.8" />
            <circle cx="240" cy="114" r="1.6" fill="#cbd5e1" opacity="0.8" />
            <circle cx="233" cy="124" r="1.6" fill="#cbd5e1" opacity="0.8" />
            <circle cx="226" cy="114" r="1.6" fill="#cbd5e1" opacity="0.8" />
            <circle cx="226" cy="88" r="1.6" fill="#cbd5e1" opacity="0.8" />
          </g>
        )}

        {/* ── Top Terminal Box Pilot LED ── */}
        <circle
          cx="117"
          cy="12"
          r="4"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.2"
          filter={isRunning ? "url(#motorLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticMotor.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticMotor;
